import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import TabsCenter from "features/CenterContent/shared/Tabs/TabsCenter";
import {
  SinglePanelBusModule as BusModule,
  PanelHeaderDivider,
  PanelModuleGrid,
  PanelScrollArea,
  PanelScrollContent,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/altTransportPanelLayout";
import { ReserveVehiclePanelActions } from "features/CenterContent/VehicleDetails/ReserveAlternativeTransportDetails/ReserveVehiclePanelActions";
import OperationalLog from "features/CenterContent/VehicleDetails/shared/OperationalLog";
import {
  VehicleSummary,
  VehicleTitle,
} from "features/CenterContent/VehicleDetails/shared/VehicleSummary";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { Module } from "shared/components/Module";
import { Text, VStack } from "shared/components/Spor";
import { reserveAlternativeTransportSchema } from "shared/types/alternativeTransport";
import * as z from "zod";

const BackupAlternativeTransportDetails = () => {
  const { vehicleUuid } = useParams();

  const { status, data: backupVehicle } = useQuery({
    queryKey: ["backupDetails", "backup", vehicleUuid],
    refetchInterval: 30000,
    queryFn: async ({ signal }) =>
      axiosClient
        .get<
          z.infer<typeof reserveAlternativeTransportSchema>
        >(`${getBackendUrl()}/alternativeTransports/backup/${vehicleUuid}`, { signal })
        .then((res) => reserveAlternativeTransportSchema.parse(res.data)),
  });

  const sectionState = useMemo(
    () => ({
      fetchStatus: status,
      isEmpty: !backupVehicle,
      emptyReason: "Fant ingen detaljer om bussen",
    }),
    [backupVehicle, status],
  );

  return (
    <>
      <TabsCenter isBackUpBus />
      <BusModule
        sectionState={sectionState}
        action1={
          backupVehicle ? (
            <ReserveVehiclePanelActions vehicle={backupVehicle} />
          ) : undefined
        }
        title={
          backupVehicle && (
            <VehicleTitle
              {...backupVehicle}
              title={`Beredskap ${backupVehicle.workShiftId}`}
            />
          )
        }
      >
        {backupVehicle && (
          <>
            <PanelHeaderDivider>
              <VehicleSummary
                variant="reserve-backup"
                {...backupVehicle}
                availableFrom={backupVehicle.shiftStartTime}
                availableTo={backupVehicle.shiftEndTime}
                location={backupVehicle.baseStopName}
                reference={
                  backupVehicle.isirkId ?? backupVehicle.referenceNumber
                }
              />
            </PanelHeaderDivider>
            <PanelScrollContent>
              <PanelScrollArea>
                <PanelModuleGrid>
                  <VStack
                    style={{
                      width: "100%",
                      padding: 0,
                      alignItems: "stretch",
                    }}
                  >
                    {backupVehicle.notes.length > 0 && (
                      <div style={{ width: "100%", padding: "0.75rem" }}>
                        <ExpandableInfoMessage
                          semantics="info"
                          title="Info fra Brudd"
                        >
                          {backupVehicle.notes.map((note) => (
                            <Text key={note}>{note}</Text>
                          ))}
                        </ExpandableInfoMessage>
                      </div>
                    )}
                    <Module title="Logg">
                      <OperationalLog
                        vehicleId={backupVehicle.id}
                        logs={backupVehicle.logs ?? []}
                      />
                    </Module>
                  </VStack>
                </PanelModuleGrid>
              </PanelScrollArea>
            </PanelScrollContent>
          </>
        )}
      </BusModule>
    </>
  );
};

export default withErrorBoundary(BackupAlternativeTransportDetails);
